import * as XLSX from "xlsx";

export const CLASSROOM_TRANSITION_MAX_FILE_BYTES = 1024 * 1024;
export const CLASSROOM_TRANSITION_MAX_ROWS = 500;

export const getClassroomTransitionRows = (response, keys) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;

  return keys.flatMap((key) => {
    if (Array.isArray(response?.[key])) return response[key];
    if (Array.isArray(response?.data?.[key])) return response.data[key];
    return [];
  });
};

const NAME_HEADERS = new Set([
  "child",
  "child name",
  "full name",
  "name",
  "pupil",
  "pupil name",
  "student",
  "student name"
]);
const FIRST_NAME_HEADERS = new Set(["first", "first name", "firstname", "forename"]);
const LAST_NAME_HEADERS = new Set(["last", "last name", "lastname", "surname"]);

export const normaliseRosterName = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

export const sortClassroomTransitionChildren = (children) =>
  [...children].sort((left, right) => left.name.localeCompare(right.name));

export const toClassroomTransitionAllocation = (child, defaultSource) =>
  child?.newChild || child?.kind === "new"
    ? { kind: "new", name: child.name, source: child.source || defaultSource }
    : { kind: "existing", childId: child.id, source: child.source || defaultSource };

const comparableName = (value) =>
  normaliseRosterName(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const getTransitionChildId = (child) =>
  child?.id || child?._id || child?.studentid || child?.studentId || "";

export const getTransitionChildName = (child) =>
  normaliseRosterName(child?.name || child?.fullName || child?.displayName);

export const normaliseTransitionChildren = (children) =>
  Array.from((Array.isArray(children) ? children : [])
    .map((child) => ({
      ...child,
      id: getTransitionChildId(child),
      name: getTransitionChildName(child)
    }))
    .filter((child) => child.id && child.name)
    .reduce((childrenById, child) => childrenById.set(child.id, child), new Map())
    .values())
    .sort((left, right) => left.name.localeCompare(right.name));

const readFileAsArrayBuffer = (file) => {
  if (typeof file?.arrayBuffer === "function") return file.arrayBuffer();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The selected file could not be read."));
    reader.onload = () => resolve(reader.result);
    reader.readAsArrayBuffer(file);
  });
};

const findColumn = (headers, allowedHeaders) =>
  headers.findIndex((header) => allowedHeaders.has(comparableName(header)));

const hasUnsafeControlCharacter = (value) => Array.from(value).some((character) => {
  const code = character.charCodeAt(0);
  return code <= 31 && ![9, 10, 13].includes(code);
});

const getNamesFromRows = (rows) => {
  const nonBlankRows = rows.filter((row) =>
    (Array.isArray(row) ? row : []).some((cell) => normaliseRosterName(cell))
  );
  if (!nonBlankRows.length) return [];

  const headers = nonBlankRows[0];
  const nameColumn = findColumn(headers, NAME_HEADERS);
  const firstNameColumn = findColumn(headers, FIRST_NAME_HEADERS);
  const lastNameColumn = findColumn(headers, LAST_NAME_HEADERS);
  const hasRecognisedHeader = nameColumn >= 0 || firstNameColumn >= 0 || lastNameColumn >= 0;
  const dataRows = hasRecognisedHeader ? nonBlankRows.slice(1) : nonBlankRows;

  return dataRows
    .map((row) => {
      if (nameColumn >= 0) return normaliseRosterName(row[nameColumn]);
      if (firstNameColumn >= 0 || lastNameColumn >= 0) {
        return normaliseRosterName([
          firstNameColumn >= 0 ? row[firstNameColumn] : "",
          lastNameColumn >= 0 ? row[lastNameColumn] : ""
        ].filter(Boolean).join(" "));
      }
      return normaliseRosterName(row[0]);
    })
    .filter(Boolean);
};

export async function parseClassroomTransitionFile(file, {
  maxBytes = CLASSROOM_TRANSITION_MAX_FILE_BYTES,
  maxRows = CLASSROOM_TRANSITION_MAX_ROWS
} = {}) {
  const filename = String(file?.name || "");
  const extension = filename.split(".").pop()?.toLowerCase();
  if (!file || !["csv", "xlsx"].includes(extension)) {
    throw new Error("Choose a CSV or XLSX file.");
  }
  if (file.size > maxBytes) {
    throw new Error(`Choose a file no larger than ${Math.round(maxBytes / 1024 / 1024)} MB.`);
  }

  try {
    const contents = await readFileAsArrayBuffer(file);
    if (extension === "xlsx") {
      const signature = new Uint8Array(contents).slice(0, 2);
      if (signature[0] !== 0x50 || signature[1] !== 0x4b) {
        throw new Error("invalid XLSX signature");
      }
    }
    const workbook = XLSX.read(contents, { type: "array", raw: false });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) throw new Error("missing worksheet");
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], {
      defval: "",
      header: 1,
      raw: false
    });
    const names = getNamesFromRows(rows);
    if (!names.length) throw new Error("empty classroom list");
    if (names.some((name) => name.length > 200 || hasUnsafeControlCharacter(name))) {
      throw new Error("invalid classroom-list content");
    }
    if (names.length > maxRows) {
      throw new Error(`Class lists may contain at most ${maxRows} children.`);
    }

    return { filename, names };
  } catch (error) {
    if (/at most/.test(error?.message || "")) throw error;
    throw new Error("This file could not be parsed as a classroom list.");
  }
}

const tokenSimilarity = (leftName, rightName) => {
  const leftTokens = comparableName(leftName).split(" ").filter(Boolean);
  const rightTokens = comparableName(rightName).split(" ").filter(Boolean);
  if (!leftTokens.length || !rightTokens.length) return 0;

  const matched = leftTokens.filter((leftToken) => rightTokens.some((rightToken) =>
    leftToken === rightToken ||
    (leftToken.length === 1 && rightToken.startsWith(leftToken)) ||
    (rightToken.length === 1 && leftToken.startsWith(rightToken)) ||
    (Math.min(leftToken.length, rightToken.length) >= 3 &&
      (leftToken.startsWith(rightToken) || rightToken.startsWith(leftToken)))
  )).length;

  return matched / Math.max(leftTokens.length, rightTokens.length);
};

export function matchUploadedChildren(names, children) {
  const availableChildren = normaliseTransitionChildren(children);
  const usedChildIds = new Set();
  const uploadedNameCounts = new Map();
  const matched = [];
  const unresolved = [];

  (Array.isArray(names) ? names : []).forEach((uploadedName, index) => {
    const name = normaliseRosterName(uploadedName);
    const nameKey = comparableName(name);
    const duplicateCount = uploadedNameCounts.get(nameKey) || 0;
    uploadedNameCounts.set(nameKey, duplicateCount + 1);

    const rowId = `upload-row-${index + 1}`;
    if (duplicateCount > 0) {
      unresolved.push({ id: rowId, uploadedName: name, matchType: "duplicate", candidates: [] });
      return;
    }

    const exactCandidates = availableChildren.filter((child) =>
      !usedChildIds.has(child.id) && comparableName(child.name) === nameKey
    );
    if (exactCandidates.length === 1) {
      usedChildIds.add(exactCandidates[0].id);
      matched.push({ ...exactCandidates[0], uploadedName: name, source: "upload" });
      return;
    }

    const candidates = availableChildren
      .filter((child) => !usedChildIds.has(child.id))
      .map((child) => ({ ...child, similarity: tokenSimilarity(name, child.name) }))
      .filter((child) => child.similarity >= 0.5)
      .sort((left, right) => right.similarity - left.similarity || left.name.localeCompare(right.name))
      .slice(0, 5)
      .map(({ similarity, ...child }) => child);

    unresolved.push({
      id: rowId,
      uploadedName: name,
      matchType: exactCandidates.length > 1 || candidates.length > 1
        ? "ambiguous"
        : candidates.length === 1
          ? "likely"
          : "none",
      candidates: exactCandidates.length > 1 ? exactCandidates : candidates
    });
  });

  return { matched, unresolved };
}
